import type { KnowledgeBase } from "@repo/app-types/entities/knowledgeBase";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { useState } from "react";
import { useUpdateKnowledgeBaseMutation } from "../../../api/entities/knowledgeBase.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

interface KnowledgeBaseNameEditFormProps {
  knowledgeBase: KnowledgeBase;
}

export function KnowledgeBaseNameEditForm({
  knowledgeBase,
}: KnowledgeBaseNameEditFormProps) {
  const dispatch = useAppDispatch();
  const [updateKnowledgeBase, { isLoading: isUpdating }] =
    useUpdateKnowledgeBaseMutation();

  const [name, setName] = useState(knowledgeBase.name);
  const [nameError, setNameError] = useState("");

  const hasNameChanged = name.trim() !== knowledgeBase.name.trim();

  const handleSave = async () => {
    if (name.trim() === "") {
      setNameError("Name is required");
      return;
    }

    setNameError("");
    try {
      await updateKnowledgeBase({
        id: knowledgeBase.id,
        data: { name: name.trim() },
      }).unwrap();

      showNotification(dispatch, {
        id: `kb-name-updated-${Date.now()}`,
        primaryMessage: "Knowledge base name updated successfully",
        type: NotificationType.SUCCESS,
      });
    } catch (err: any) {
      showNotification(dispatch, {
        id: `kb-name-update-error-${Date.now()}`,
        primaryMessage: `Failed to update name: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  const handleCancel = () => {
    setName(knowledgeBase.name);
    setNameError("");
  };

  return (
    <div>
      <Label size={Size.SMALL}>Knowledge base name</Label>
      <div className="mt-2">
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError("");
          }}
          placeholder="Enter knowledge base name"
          errorDescription={nameError}
          inputClassName="max-w-[400px]"
          maxLength={100}
        />
      </div>
      {hasNameChanged && (
        <SaveAndCancelButtons
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isUpdating}
          isSaveDisabled={!name.trim()}
        />
      )}
    </div>
  );
}
